/* eslint-disable react/jsx-indent */

import AccountBox from '@mui/icons-material/AccountBox';
import AutoAwesome from '@mui/icons-material/AutoAwesome';
import EditIcon from '@mui/icons-material/Edit';
import LocationOn from '@mui/icons-material/LocationOn';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import Avatar from '@mui/material/Avatar';
import Box from '@mui/material/Box';
import IconButton from '@mui/material/IconButton';
import Menu from '@mui/material/Menu';
import MenuItem from '@mui/material/MenuItem';
import Paper from '@mui/material/Paper';
import Typography from '@mui/material/Typography';
import Moment from 'moment';
import PropTypes from 'prop-types';
import React from 'react';
import { useSelector } from 'react-redux';

import {
  Block,
  Group,
  NoAccounts,
} from '@mui/icons-material';
import {
  Alert,
  Chip,
  Divider,
  useMediaQuery,
} from '@mui/material';
import Grid from '@mui/material/Grid';
import ProfileCardLoading from './components/ProfileCardLoading';

import LocationChip from '../../../../../components/LocationChip';
import NoteItem from '../../../../../components/NoteItem';
import User from '../../../../../components/User';
import { getColorForImportance } from '../../../../../utils/enumHelper';
import themeUtil from '../../../../../utils/theme';
import HorseLodgingListItem from '../LodgingsListItem';
import QuestriSuggestionsDialog from '../QuestriSuggestionsDialog';

const ProfileCardComponent = ({
  id,
  openAddEditNoteModal,
  currentHorseLocations,
  horseOwners,
  setTab,
  setOpenEditMode,
  condensed,
}) => {
  // Redux state
  const horse = useSelector((state) => state.horse.horse);
  const currentHorseLodging = useSelector((state) => state.horse.currentHorseLodging);
  const isFetchingHorse = useSelector((state) => state.horse.isFetchingHorse);
  const isFetchingHorseLodgings = useSelector((state) => state.horse.isFetchingHorseLodgings);
  const permissions = useSelector((state) => state.user.permissions);
  const fullScreen = useMediaQuery(themeUtil.breakpoints.down('md'));

  // React state
  const [anchorEl, setAnchorEl] = React.useState(null);
  const [defaultOpenLodging, setDefaultOpenLodging] = React.useState(false);
  const [suggestionsDialogOpen, setSuggestionsDialogOpen] = React.useState(false);

  React.useEffect(() => {
    setDefaultOpenLodging(false);
  }, [id]);

  const handleMenuClick = (event) => {
    setAnchorEl(event.currentTarget);
  };
  const handleCloseMenu = () => {
    setAnchorEl(null);
  };

  return (isFetchingHorse
    ? <ProfileCardLoading />
    : (
      <>
        <Box
          sx={(currentHorseLodging.inactive || horse.activeNote)
            ? {
              position: 'relative',
              top: '-8px',
            }
            : {}}
        >
          {currentHorseLodging.inactive && (
            <Alert
              severity="error"
              sx={{
                width: '100%',
                flex: 1,
                '& .MuiAlert-message': { flex: 1 },
                position: 'relative',
                top: '8px',
              }}
            >INACTIVE since {Moment(currentHorseLodging.startDate).format('ll')}
            </Alert>
          )}
          {
            horse.activeNote && (
              <Alert
                icon={false}
                variant="outlined"
                severity={getColorForImportance(horse.activeNote.importance)}
                sx={{
                  bgcolor: 'background.paper',
                  width: '100%',
                  flex: 1,
                  '& .MuiAlert-message': { flex: 1 },
                  position: 'relative',
                  top: '8px',
                  py: 0,
                }}
              >
                <Box
                  sx={{
                    width: '100%',
                    flex: 1,
                  }}
                >
                  <NoteItem
                    divider={false}
                    pinnedHorseNoteId={horse.activeNote.id}
                    note={horse.activeNote}
                    horseId={horse.id}
                    includeUserInfo={false}
                    condensed
                    onEdit={() => openAddEditNoteModal(horse.activeNote, '', null)}
                  />
                </Box>
              </Alert>
            )
          }
          <Paper
            sx={(horse.activeNote || currentHorseLodging.inactive)
              ? {
                borderRadius: '0 0 12px 16px',
                p: 2,
              }
              : {
                borderRadius: 2,
                p: 2,
              }}
          >
            <Grid container alignItems="flex-start" justifyContent="space-between" spacing={1}>
              <Grid sx={{ flex: 1 }}>
                <Grid container alignItems="center" spacing={2}>
                  <Grid>
                    <Avatar
                      sx={[condensed
                        ? { width: 40 }
                        : { width: 100 }, condensed
                        ? { height: 40 }
                        : { height: 100 }]}
                      alt={horse.name}
                      src={`https://${horse.picture
                        ? `${horse.picture}?cacheBust=${new Date()}`
                        : `i0.wp.com/cdn.auth0.com/avatars/${((horse && horse.name) || 'A').charAt(0).toLowerCase()}.png`}`}
                      aria-label={`${horse.name} avatar`}
                    />
                  </Grid>
                  <Grid>
                    <Typography
                      variant={condensed
                        ? 'h4'
                        : 'h3'}
                      sx={{
                        maxWidth: condensed
                          ? 200
                          : 250,
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        display: '-webkit-box',
                        WebkitLineClamp: '2', // Truncate after 2 lines
                        WebkitBoxOrient: 'vertical',
                      }}
                    >{horse.name}
                    </Typography>
                    <Typography
                      variant={condensed
                        ? 'h5'
                        : 'h4'}
                      sx={{
                        fontWeight: 300,
                        maxWidth: condensed
                          ? 200
                          : 250,
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        display: '-webkit-box',
                        WebkitLineClamp: '1', // Truncate after 2 lines
                        WebkitBoxOrient: 'vertical',
                      }}
                    >{horse.showName || ''}
                    </Typography>
                  </Grid>
                  {
                    !fullScreen && !condensed && (
                      <Grid
                        sx={{
                          flex: 1,
                          display: 'flex',
                          gap: 2,
                          flexWrap: 'wrap',
                          justifyContent: 'flex-end',
                          alignItems: 'stretch',
                        }}
                      >
                        <Box
                          sx={(theme) => ({
                            display: 'flex',
                            flexDirection: 'column',
                            gap: 1,
                            borderRadius: 2,
                            border: `1px solid ${theme.palette.divider}`,
                            '&:hover': { opacity: 0.75 },
                            p: 2,
                            cursor: setTab
                              ? 'pointer'
                              : 'inherit',
                          })}
                          onClick={() => {
                            if (setTab) {
                              setTab('locations')
                            }
                          }}
                        >
                          <Box
                            sx={{
                              display: 'flex',
                              alignItems: 'center',
                              gap: 1,
                            }}
                          >
                            <LocationOn />
                            <Typography variant="subtitle2" color="text.secondary">
                              Current Location
                            </Typography>
                          </Box>
                          <Box
                            sx={{
                              display: 'flex',
                              alignItems: 'center',
                              gap: 1,
                              maxWidth: 225,
                              overflow: 'auto',
                              flexWrap: 'wrap',
                            }}
                          >
                            {
                              (currentHorseLocations && currentHorseLocations.length > 0) && currentHorseLocations.map((hl) => (
                                <LocationChip key={`horse-location-${hl.id}`} location={hl.location} id={`horse-location-${hl.id}`} />
                              ))
                            }
                            {
                              (!currentHorseLocations || currentHorseLocations.length === 0) && (<Typography>No Location</Typography>)
                            }
                          </Box>
                        </Box>
                        <Box
                          sx={(theme) => ({
                            display: 'flex',
                            flexDirection: 'column',
                            gap: 1,
                            borderRadius: 2,
                            border: `1px solid ${theme.palette.divider}`,
                            '&:hover': { opacity: 0.75 },
                            p: 2,
                            cursor: setTab
                              ? 'pointer'
                              : 'inherit',
                          })}
                          onClick={() => {
                            if (setTab) {
                              setTab('users')
                            }
                          }}
                        >
                          <Box
                            sx={{
                              display: 'flex',
                              alignItems: 'center',
                              gap: 1,
                            }}
                          >
                            <AccountBox />
                            <Typography variant="subtitle2" color="text.secondary">
                              Horse Owner
                            </Typography>
                          </Box>
                          <Box
                            sx={{
                              display: 'flex',
                              alignItems: 'center',
                              gap: 1,
                              maxWidth: 225,
                              overflow: 'auto',
                              flexWrap: 'wrap',
                            }}
                          >
                            {
                              (horseOwners && horseOwners.length > 0) && (horseOwners.map((ha) => (
                                <User key={`horse-owner-${ha.id}`} user={ha.user} secondary="owner" chip disableGutters />
                              ))
                              )
                            }
                            {
                              (!horseOwners || horseOwners.length === 0) && (<Chip size="small" icon={<NoAccounts color="secondary" />} label="No Owner" />)
                            }
                          </Box>
                        </Box>
                        <Box
                          sx={() => ({
                            maxWidth: 225,
                            display: 'flex',
                            flexDirection: 'column',
                            gap: 1,
                          })}
                        >
                          <HorseLodgingListItem
                            border
                            id={id}
                            horseLodging={currentHorseLodging}
                            isAdmin={false}
                            defaultOpen={defaultOpenLodging}
                            containerSx={{ height: '100%' }}
                          />
                        </Box>
                      </Grid>
                    )
                  }

                </Grid>
              </Grid>
              {
                !condensed && (
                  <Grid align="right">
                    <IconButton
                      // edge="end"
                      aria-label="horse profile settings"
                      id="menu-btn"
                      aria-owns={anchorEl
                        ? 'menu-appbar'
                        : null}
                      aria-haspopup="true"
                      onClick={handleMenuClick}
                    >
                      <MoreVertIcon size="medium" />
                    </IconButton>
                    <Menu
                      id="horse-profile-menu"
                      anchorEl={anchorEl}
                      open={Boolean(anchorEl)}
                      onClose={handleCloseMenu}
                    >
                      <MenuItem onClick={() => { setOpenEditMode(true); }}>
                        <EditIcon color="secondary" sx={{ mr: 2 }} /> Edit Profile Fields
                      </MenuItem>
                      <MenuItem onClick={() => { setTab('locations'); }}>
                        <LocationOn color="secondary" sx={{ mr: 2 }} /> Manage Horse Location
                      </MenuItem>
                      {(permissions.owner || permissions.admin) && (
                        <MenuItem onClick={() => { setTab('users') }}>
                          <Group color="secondary" sx={{ mr: 2 }} /> Manage Owners & Users
                        </MenuItem>
                      )}
                      {(permissions.owner || permissions.admin) && (
                        <MenuItem
                          onClick={() => {
                            setSuggestionsDialogOpen(true);
                            handleCloseMenu();
                          }}
                        >
                          <AutoAwesome
                            sx={{
                              mr: 2,
                              color: '#FF6600',
                            }}
                          /> Questri Suggestions
                        </MenuItem>
                      )}
                      {currentHorseLodging.inactive === false && <Divider />}
                      {currentHorseLodging.inactive === false && (
                        <MenuItem onClick={() => { setDefaultOpenLodging(true); setAnchorEl(null); }}>
                          <Block color="error" sx={{ mr: 2 }} /> Set Horse INACTIVE
                        </MenuItem>
                      )}
                    </Menu>
                  </Grid>
                )
              }
              {
                (!isFetchingHorse && !isFetchingHorseLodgings && (!horse || !horse.id)) && (
                  <Grid size={12}>
                    <Alert severity="warning">
                      Horse information not loaded correctly, you might have issues updating horse information.
                      Try refreshing your browser window, if this warning persists, contact support.
                    </Alert>
                  </Grid>
                )
              }
            </Grid>
          </Paper>
        </Box>
        <QuestriSuggestionsDialog
          open={suggestionsDialogOpen}
          onClose={() => setSuggestionsDialogOpen(false)}
        />
      </>
    ));
};

ProfileCardComponent.propTypes = {
  id: PropTypes.string.isRequired,
  openAddEditNoteModal: PropTypes.func.isRequired,
  currentHorseLocations: PropTypes.array.isRequired,
  horseOwners: PropTypes.array.isRequired,
  setTab: PropTypes.func,
  setOpenEditMode: PropTypes.func,
  condensed: PropTypes.bool,
};

ProfileCardComponent.defaultProps = {
  setTab: null,
  setOpenEditMode: () => { },
  condensed: false,
}
export default ProfileCardComponent;
